<template>
  <header :class="['header', 'header__sticky']">
    <div class="place-items-between container">
      <nuxt-link to="/" class="logo">
        <nuxt-img
          :src="$device.isMobile ? '/images/paribu-mobile.svg' : '/images/paribu.svg'"
          alt="Paribu Logo"
          :height="$device.isMobile ? 13 : 20"
        />
        <nuxt-img
          :src="$device.isMobile ? '/images/vector.svg' : '/images/vector.svg'"
          alt=""
          :height="$device.isMobile ? 13 : 20"
        />
        <p class="header__title">DESTEK</p>

        <nuxt-img
          :src="$device.isMobile ? '/images/vector.svg' : '/images/vector.svg'"
          alt=""
          :height="$device.isMobile ? 13 : 20"
        />
      </nuxt-link>

      <!-- Mobile Navigation -->
      <nav v-if="$device.isMobile" class="mobile-nav">
        <p-icon
          icon="search"
          size="md"
          color="fg-primary"
          class="search-icon"
          @click="showSearchOverlay"
        />

        <p-icon
          icon="hamburger"
          size="xs"
          color="fg-primary"
          class="mt-4 ml-12"
          @click="toggleMobileDrawer()"
        />
      </nav>

      <!-- Desktop Navigation -->
      <div v-else class="header-extra">
        <p-advanced-search
          v-if="!isHome"
          :placeholder="$t('general.search')"
          :enable-global-shortcut="true"
        />

        <div v-if="isHome && !user.isAuthenticated" class="download-app-container">
          <p-button variant="filled" class="download-app-button">
            <span>{{ $t('general.downloadApp') }}</span>
            <p-icon icon="mobile-phone" size="xmd" color="fix-white" />
          </p-button>

          <div class="qrcode-popup">
            <div class="qrcode-popup__text">
              <p class="f-utility-16 t-fg-tertiary" v-text="$t('downloadApp.qrCodeTitle')" />
            </div>
            <div class="qrcode-popup__code">
              <div class="qrcode-popup__code-container">
                <NuxtImg
                  src="/images/paribu-app-download.svg"
                  width="150"
                  height="150"
                  alt="Paribu App Download QR"
                />
                <nuxt-img
                  src="/images/border-stroke.svg"
                  class="qrcode-popup__code-border"
                  width="190"
                  height="190"
                  alt="QR Border"
                />
              </div>
            </div>
            <div class="qrcode-popup__stores">
              <a
                href="https://apps.apple.com/tr/app/paribu-bitcoin-al%C4%B1m-sat%C4%B1m/id1448200352?l=tr"
                target="_blank"
              >
                <NuxtImg src="/images/app-store.svg" width="113" height="30" alt="App Store" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.paribu.app"
                target="_blank"
              >
                <NuxtImg src="/images/google-play.svg" width="113" height="30" alt="Google Play" />
              </a>
            </div>
          </div>
        </div>

        <div
          ref="paribuSolutionsRef"
          :class="{ 'solutions-item': 1, 'solutions-item-active': showParibuSolutionsOverlay }"
          @click="showParibuSolutionsOverlay = !showParibuSolutionsOverlay"
        >
          {{ $t('general.paribuSolutions') }}
        </div>
        <div v-show="showParibuSolutionsOverlay" class="solutions-overlay">
          <paribu-solutions :items="paribuProducts" />
        </div>

        <div
          ref="languagePopupRef"
          :class="{ 'world-icon-container': 1, 'is-open': showLangPopup }"
          @click="showLangPopup = !showLangPopup"
        >
          <p-icon icon="world" color="fg-primary" size="mdw" />

          <div class="world-icon-container--popup">
            <button
              type="button"
              class="world-icon-container--popup__btn"
              @click="changeLanguage('tr-TR')"
            >
              {{ $t('language.turkish') }}
              <p-icon v-if="locale == 'tr-TR'" icon="check" color="fg-accent" size="sm" />
            </button>
            <button
              type="button"
              class="world-icon-container--popup__btn"
              @click="changeLanguage('en-US')"
            >
              {{ $t('language.english') }}
              <p-icon v-if="locale == 'en-US'" icon="check" color="fg-accent" size="sm" />
            </button>
          </div>
        </div>

        <template v-if="user.isAuthenticated">
          <div class="profile-icon-container">
            <nuxt-link
              class="header__link--notactive header__profile--link"
              @click="showAccountHover = !showAccountHover"
            >
              <div class="header__profile--wrapper">
                <div class="header__profile">
                  <img
                    src="assets/images/account/profilePicture.png"
                    class="user-detail__avatar__img"
                    alt="user profile picture"
                  />
                </div>
                <account-hover v-show="showAccountHover" />
              </div>
            </nuxt-link>
          </div>
        </template>
        <template v-else></template>
      </div>
    </div>

    <!-- Mobile Menu -->
    <div v-if="showMobileMenu && $device.isMobile">
      <mobile-navigation @on-close="showMobileMenu = false" />
    </div>

    <!-- Mobile Drawer -->
    <div v-show="showMobileDrawer" class="mobile-drawer-bg"></div>
    <div :class="{ 'mobile-drawer': 1, 'mobile-drawer-active': showMobileDrawer }">
      <div class="d-flex justify-content-end p-16 mb-16">
        <p-icon icon="close" color="fg-primary" @click="toggleMobileDrawer()" />
      </div>
      <div class="px-16">
        <template v-if="user.isAuthenticated">
          <div class="account-hover">
            <div class="account-hover__user">
              <div class="account-hover__user--card">
                <div class="account-hover__user--card__name">{{ user.info.name }}</div>

                <div class="account-hover__user--card__verified">
                  <p-icon icon="shield-verified" color="fg-accent" size="sm" class="mr-4" />
                  {{ $t('general.verified') }}
                </div>
              </div>

              <p-icon
                v-if="$device.isDesktop"
                icon="arrow-top-right"
                color="fg-primary"
                size="sm"
              />
            </div>

            <p-divider v-if="$device.isDesktop" class="my-16" />
            <div v-else class="my-24"></div>

            <div class="account-hover__buttons">
              <p-button
                size="md"
                class="full-width justify-content-start p-0 mt-16"
                button-type="ghost"
              >
                <p-icon icon="exit" size="sm" color="fg-secondary" />
                {{ $t('general.signout') }}
              </p-button>

              <p-button
                size="md"
                class="full-width justify-content-start p-0 mt-16"
                button-type="ghost"
              >
                <p-icon icon="list-check" size="sm" color="fg-secondary" />
                {{ $t('general.supportTickets') }}
                <p-badge size="lg" text="0" />
              </p-button>

              <p-button
                size="md"
                class="full-width justify-content-start p-0 mt-16"
                button-type="ghost"
                @click="
                  () => {
                    smartRouter.go('/settings/language-preferences')
                    toggleMobileDrawer()
                  }
                "
              >
                <p-icon icon="world" size="sm" color="fg-secondary" />
                {{ $t('general.languagePreferences') }}
              </p-button>
            </div>
          </div>
        </template>
        <template v-else>
          <p-button
            size="md"
            class="full-width justify-content-start p-0 mt-16"
            button-type="ghost"
            @click="
              () => {
                smartRouter.go('/settings/language-preferences')
                toggleMobileDrawer()
              }
            "
          >
            <p-icon icon="world" size="sm" color="fg-secondary" />
            {{ $t('general.languagePreferences') }}
          </p-button>
        </template>
        <p-divider class="mt-32" />
      </div>
      <paribu-solutions :items="paribuProducts" />
    </div>
  </header>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
// @ts-ignore - Vue SFC default export is provided at build
import MobileNavigation from './MobileNavigation/index.vue'
// @ts-ignore - Vue SFC default export is provided at build
import ParibuSolutions from './ParibuSolutions/ParibuSolutions.vue'
import PAdvancedSearch from '~/components/pages/Search/PAdvancedSearch/PAdvancedSearch.vue'
import { useUserStore } from '@/store/user'
import { useSearchOverlay } from '@/composables/useSearchOverlay'
import { smartRouter } from '@/composables'
// import { useTenant } from '@/composables/useTenant'

import logomark from '@/assets/images/product/logomark.svg'

smartRouter.init()

const { locale, t } = useI18n()

const { user } = useUserStore()

const route = useRoute()

const isHome = computed(() => route.path === '/' || route.name === 'index')
const showMobileMenu = ref(false)
const showParibuSolutionsOverlay = ref(false)
const showAccountHover = ref(false)
const showMobileDrawer = ref(false)
const showLangPopup = ref(false)

const paribuSolutionsRef = ref<HTMLElement>()
const languagePopupRef = ref<HTMLElement>()

const { showSearchOverlay } = useSearchOverlay()

const handleClickOutside = (event: MouseEvent) => {
  // Paribu Solutions Overlay
  if (paribuSolutionsRef.value && !paribuSolutionsRef.value.contains(event.target as Node)) {
    showParibuSolutionsOverlay.value = false
  }

  // Language Popup
  if (languagePopupRef.value && !languagePopupRef.value.contains(event.target as Node)) {
    showLangPopup.value = false
  }
}

const changeLanguage = (lang: string) => {
  locale.value = lang
  useCookie('locale').value = lang
  window.location.reload()
}

const toggleMobileDrawer = () => {
  showMobileDrawer.value = !showMobileDrawer.value

  if (showMobileDrawer.value) {
    document.documentElement.style.overflow = 'hidden'
  } else {
    document.documentElement.removeAttribute('style')
  }
}

const paribuProducts = computed(() => [
  {
    icon: logomark,
    heading: 'Paribu',
    description: t('paribuProducts.cryptoTrading'),
    link: '/',
  },
  {
    icon: logomark,
    heading: 'Paribu Self',
    description: t('paribuProducts.secureStorage'),
    link: '/paribu-self',
  },
  {
    icon: logomark,
    heading: 'Paribu Custody',
    description: t('paribuProducts.institutionalCustody'),
    link: '/custody',
  },
  {
    icon: logomark,
    heading: 'Paribu Money',
    description: t('paribuProducts.digitalWallet'),
    link: '/money',
  },
  {
    icon: logomark,
    heading: 'Paribu Stock',
    description: t('paribuProducts.stockInvestment'),
    link: '/paribu-stock',
  },
  {
    icon: logomark,
    heading: 'Paribu Pass',
    description: t('paribuProducts.cryptoEventTickets'),
    link: '/pass',
  },
])

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
